// =============================================================================
// FILE: packages/renderer/src/config/pricing.ts
// PURPOSE: Single source of truth for BIM OPS license tiers, pricing,
//          feature gates, and AI model routing.
//
// Master Command Pricing Model (RCD² v2):
//   Monitor License        — $49/mo  (read-only child license)
//   Empowered Technician   — $199/mo
//   Certified Professional — $599/mo
//   Enterprise Intelligence — $1,999/mo
//   Global Master Engine   — $4,999/mo + royalties
// =============================================================================

export type LicenseTier =
  | 'trial'
  | 'monitor'
  | 'technician'
  | 'contractor'
  | 'designer'
  | 'global_master';

export type BillingCycle = 'monthly' | 'annual';

export interface TierFeature {
  label:    string;
  included: boolean;
  note?:    string;
}

/** Full tier definition — used by PricingPage and all tier-gate checks */
export interface TierConfig {
  id:                 LicenseTier;
  name:               string;
  tagline:            string;
  badge?:             string;          // e.g. "Most Popular"
  monthlyUSD:         number;
  annualUSD:          number;
  royaltyPerPackage:  number;          // 0 for most tiers
  popular?:           boolean;         // highlight treatment
  highlight?:         boolean;         // alias for popular
  accentColor:        string;
  glowColor:          string;
  readOnly?:          boolean;         // monitor / child licenses
  royalty?:           boolean;         // requires royalty agreement
  aiModel:            string;          // AI router key
  features:           TierFeature[];
}

/** 20% off for verified RCDD credential holders */
export const RCDD_DISCOUNT_PCT = 0.20;

/** $5,999 perpetual "Legacy" license — bug-free lifetime satisfaction guarantee.
 *  Standalone (not a subscription tier) so it never affects tier-gate logic. */
export const PERPETUAL_LICENSE = {
  id:        'legacy',
  name:      'RCD² Legacy License',
  priceUSD:  5999,
  tagline:   'Own it forever. One payment.',
  guarantee: 'Bug-Free Guarantee — if you find a bug in your version, we fix it free, forever.',
  note:      'Lifetime satisfaction on the current feature set. New features ship in future versions.',
} as const;

/** Effective price for a given cycle + RCDD discount */
export function effectivePrice(
  tier: TierConfig,
  cycle: BillingCycle,
  rcddVerified: boolean,
): number {
  const base = cycle === 'monthly' ? tier.monthlyUSD : tier.annualUSD;
  return rcddVerified ? Math.round(base * (1 - RCDD_DISCOUNT_PCT)) : base;
}

/** Per-month display price for annual billing */
export function annualMonthlyDisplay(tier: TierConfig, rcddVerified: boolean): number {
  return Math.round(effectivePrice(tier, 'annual', rcddVerified) / 12);
}

/** Annual savings vs paying monthly for 12 months */
export function annualSavings(tier: TierConfig, rcddVerified: boolean = false): number {
  const monthly12  = effectivePrice(tier, 'monthly', rcddVerified) * 12;
  const annualCost = effectivePrice(tier, 'annual',  rcddVerified);
  return monthly12 - annualCost;
}

// =============================================================================
// ── TIER DEFINITIONS (array — PricingPage iterates with .map) ────────────────
// =============================================================================

export const TIERS: TierConfig[] = [
  {
    id:                'trial',
    name:              'Free Trial',
    tagline:           'Explore BIM OPS risk-free for 14 days.',
    monthlyUSD:        0,
    annualUSD:         0,
    royaltyPerPackage: 0,
    accentColor:       '#94A3B8',
    glowColor:         'rgba(148,163,184,0.12)',
    aiModel:           'gemini-flash',
    features: [
      { label: 'Dashboard & System Overview',    included: true  },
      { label: 'Up to 2 Projects',               included: true  },
      { label: 'Up to 10 Assets',                included: true  },
      { label: 'Emmy AI (10 msgs/day)',           included: true  },
      { label: 'Parts Library (view only)',       included: false },
      { label: 'Brand Switcher',                  included: false },
    ],
  },

  {
    id:                'monitor',
    name:              'Monitor License',
    tagline:           'Fair-Bid Auditing & Read-Only Oversight.',
    monthlyUSD:        49,
    annualUSD:         490,
    royaltyPerPackage: 0,
    readOnly:          true,
    accentColor:       '#60A5FA',
    glowColor:         'rgba(96,165,250,0.12)',
    aiModel:           'gemini-flash',
    features: [
      { label: 'Read-only view of all project data', included: true  },
      { label: 'Live cost & diagram monitoring',     included: true  },
      { label: 'Auditor\'s Wins Feed (R² anomalies)', included: true },
      { label: 'Monitor Dashboard — Spaceship UI',   included: true  },
      { label: 'Edit / create / delete permissions', included: false },
    ],
  },

  {
    id:                'technician',
    name:              'Empowered Technician',
    tagline:           'Field productivity and real-time dashboard.',
    monthlyUSD:        199,
    annualUSD:         1990,
    royaltyPerPackage: 0,
    accentColor:       '#94A3B8',
    glowColor:         'rgba(148,163,184,0.15)',
    aiModel:           'gemini-flash',
    features: [
      { label: 'Full Dashboard & Asset Management',  included: true  },
      { label: 'Unlimited Projects & Buildings',     included: true  },
      { label: 'Emmy AI Advisor (50 msgs/day)',      included: true  },
      { label: 'Schedule & Gantt Chart',             included: true  },
      { label: 'Field Ops QR scanning',              included: true  },
      { label: 'Coach Validation',                   included: false },
      { label: 'Parts Library (add custom)',         included: false },
      { label: 'Global Brand Switcher',              included: false },
    ],
  },

  {
    id:                'contractor',
    name:              'Certified Professional',
    tagline:           'Quality Control and Auto-Pilot for RCDDs.',
    badge:             'Most Popular',
    monthlyUSD:        599,
    annualUSD:         5990,
    royaltyPerPackage: 0,
    popular:           true,
    highlight:         true,
    accentColor:       '#25B5A8',
    glowColor:         'rgba(37,181,168,0.18)',
    aiModel:           'claude-sonnet',
    features: [
      { label: 'Everything in Empowered Technician', included: true  },
      { label: 'Emmy AI Advisor (Unlimited)',         included: true  },
      { label: 'Coach Validation & QA Checks',       included: true  },
      { label: 'Auto-Report (Billing Generator)',     included: true  },
      { label: 'Parts Library + Custom Brands',      included: true  },
      { label: 'Global Brand Switcher',              included: true  },
      { label: 'RCDD Certified Badge',               included: true  },
      { label: 'M365 Mail Intelligence',             included: true  },
    ],
  },

  {
    id:                'designer',
    name:              'Enterprise Intelligence',
    tagline:           'Full Ingestion Engine and Autonomous Filing.',
    badge:             'Full Power',
    monthlyUSD:        1999,
    annualUSD:         19990,
    royaltyPerPackage: 50,
    accentColor:       '#FCD34D',
    glowColor:         'rgba(252,211,77,0.12)',
    aiModel:           'claude-opus',
    features: [
      { label: 'Everything in Certified Professional', included: true  },
      { label: 'Smart Ingestion Engine (SIE)',         included: true  },
      { label: 'Autonomous Document Filing',           included: true  },
      { label: 'Full Brand Switcher + Recalculate',   included: true  },
      { label: 'Global Command Centre',               included: true  },
      { label: 'Multi-user team support',             included: true  },
      { label: 'Priority Support (< 4h)',             included: true  },
      { label: '$50 Royalty per Certified Package',   included: true  },
    ],
  },

  {
    id:                'global_master',
    name:              'Global Master Engine',
    tagline:           'Run your entire bidding department on RCD².',
    badge:             '🌍 Global',
    monthlyUSD:        4999,
    annualUSD:         49990,
    royaltyPerPackage: 0,
    royalty:           true,
    accentColor:       '#F59E0B',
    glowColor:         'rgba(245,158,11,0.14)',
    aiModel:           'claude-opus-4',
    features: [
      { label: 'Everything in Enterprise Intelligence', included: true },
      { label: 'Pursuit Agent (auto-bid discovery)',   included: true  },
      { label: 'Executive Stationery & Outreach',     included: true  },
      { label: 'Founder\'s Gatekeeper (IP Control)',   included: true  },
      { label: 'VR Digital Twin Walkthroughs',        included: true  },
      { label: 'White-Glove Founder Onboarding',      included: true  },
      { label: 'Drone Integration (Phase 2)',         included: true  },
      { label: 'Royalty licensing terms apply',       included: true  },
    ],
  },
];

/** Record version for O(1) lookups by tier ID */
export const TIER_MAP: Record<LicenseTier, TierConfig> = Object.fromEntries(
  TIERS.map((t) => [t.id, t]),
) as Record<LicenseTier, TierConfig>;

/** Ordered list for pricing page display */
export const TIER_ORDER: LicenseTier[] = ['monitor', 'technician', 'contractor', 'designer', 'global_master'];

/** Short code used in license keys */
export const TIER_CODES: Record<LicenseTier, string> = {
  trial:        'T',
  monitor:      'M',
  technician:   'TK',
  contractor:   'C',
  designer:     'D',
  global_master: 'GM',
};

/** Legacy alias */
export type PricingTier = TierConfig;
